import { useEffect, useState } from 'react';
import { rotuloDoDia } from '../utils';
import type { ResumoResposta } from '../types';

interface Props {
  dados: ResumoResposta | null;
  carregando: boolean;
  /** Guarda o estado recolhido no aparelho, por evento. */
  eventoId?: number;
}

function numero(valor: number | null | undefined): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('pt-BR');
}

// "R$ 0,00" quando a função não mandou nada (nunca inventamos valor).
function dinheiro(valor: string | null | undefined): string {
  return typeof valor === 'string' && valor.trim() ? valor : 'R$ 0,00';
}

function chaveRecolhido(eventoId?: number): string {
  return `agente_hoje_recolhido_${eventoId ?? 'evento'}`;
}

function lerRecolhido(eventoId?: number): boolean {
  try {
    return window.localStorage.getItem(chaveRecolhido(eventoId)) === '1';
  } catch {
    return false;
  }
}

function Contagem({
  valor,
  rotulo,
  apoio,
}: {
  valor: string;
  rotulo: string;
  apoio?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="truncate text-[20px] font-extrabold leading-tight tabular-nums text-foreground-950">
        {valor}
      </p>
      <p className="mt-0.5 truncate text-[12px] font-medium text-foreground-600">{rotulo}</p>
      {apoio && <p className="truncate text-[12px] text-foreground-500">{apoio}</p>}
    </div>
  );
}

// Cartão "Hoje": o resumo do dia que o agente devolve, com 4 números de apoio.
// O cabeçalho recolhe e expande (a memória fica no aparelho, por evento).
export default function CartaoHoje({ dados, carregando, eventoId }: Props) {
  const [recolhido, setRecolhido] = useState<boolean>(() => lerRecolhido(eventoId));

  useEffect(() => {
    setRecolhido(lerRecolhido(eventoId));
  }, [eventoId]);

  function alternar() {
    setRecolhido((v) => {
      const proximo = !v;
      try {
        window.localStorage.setItem(chaveRecolhido(eventoId), proximo ? '1' : '0');
      } catch {
        /* sem armazenamento: só desta vez */
      }
      return proximo;
    });
  }

  if (carregando) {
    return (
      <section className="rounded-[16px] bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <div className="h-3 w-40 esqueleto rounded-full bg-background-200/70" />
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-3.5 esqueleto rounded-full bg-background-200/60 ${
                i === 2 ? 'w-2/3' : 'w-full'
              }`}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!dados) return null;

  const linhas = dados.resumo
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const c = dados.cartoes ?? null;
  const dia = rotuloDoDia(dados.dia);
  const rotuloPessoas = c?.publico === 'campista' ? 'Campistas' : 'Ingressos';
  const meta = c?.meta ?? null;
  const pct = meta && meta.pct !== null && meta.pct !== undefined ? Number(meta.pct) : null;
  const faltam = meta?.faltam_pessoas ?? null;

  const linhasVisiveis = recolhido ? linhas.slice(0, 1) : linhas;

  return (
    <section className="rounded-[16px] bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <button
        type="button"
        onClick={alternar}
        aria-expanded={!recolhido}
        className="aperta flex min-h-[24px] w-full cursor-pointer items-center justify-between gap-2 text-left"
      >
        <span className="min-w-0 text-[12px] font-semibold tracking-wide text-foreground-500">
          RESUMO DE HOJE{dia ? ` · ${dia}` : ''}
        </span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-400">
          <i
            className={recolhido ? 'ri-arrow-right-s-line text-lg' : 'ri-arrow-down-s-line text-lg'}
            aria-hidden
          />
        </span>
      </button>

      <div className="mt-2 space-y-1">
        {linhasVisiveis.map((linha, i) => (
          <p key={i} className="text-[15px] leading-relaxed text-foreground-800">
            {linha}
          </p>
        ))}
      </div>

      {!recolhido && c && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4">
            <Contagem
              valor={numero(c.com_vaga)}
              rotulo={rotuloPessoas}
              apoio={c.vagas !== null && c.vagas !== undefined ? `de ${numero(c.vagas)}` : undefined}
            />
            <Contagem
              valor={numero(c.novas_7_dias)}
              rotulo="Novas em 7 dias"
              apoio={`hoje: ${numero(c.novas_hoje)}`}
            />
            <Contagem valor={dinheiro(c.recebido)} rotulo="Recebido" />
            <Contagem valor={dinheiro(c.a_receber)} rotulo="A receber" />
          </div>

          {c.equipe && (
            <p className="mt-3 text-[13px] text-foreground-500">
              Equipe: {numero(c.equipe.com_vaga)} inscritos (conta separada)
            </p>
          )}

          {Number(c.suplentes) > 0 && (
            <p className="mt-1 text-[13px] text-foreground-500">
              {numero(c.suplentes)}{' '}
              {Number(c.suplentes) === 1 ? 'suplente esperando' : 'suplentes esperando'} vaga
            </p>
          )}

          {meta && pct !== null && (
            <div className="mt-3">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-primary-500 transition-[width] duration-300 ease-out"
                  style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                />
              </div>
              <p className="mt-1.5 text-[13px] text-foreground-600">
                {Number(faltam) === 0
                  ? 'Meta batida 🎉'
                  : `Meta: faltam ${numero(faltam ?? 0)} ${
                      Number(faltam) === 1 ? 'pessoa' : 'pessoas'
                    }`}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}